import React from "react";

import "./overview_tab.less";
import { WorkloadTable } from "@src/components/workload_table";
import { REQUEST_STATUS } from "@src/constants";
import { AdditionalInformationCard } from "@src/features/role_request_details/components/additional_information_card";
import { AdditionalInformationCardProps } from "@src/features/role_request_details/components/additional_information_card/additional_information_card";
import { JointVentureCheckbox } from "@src/features/role_request_details/components/joint_venture_checkbox/index";
import { ProposedEmployeeInformationCard } from "@src/features/role_request_details/components/proposed_employee_information_card/index";
import { RoleRequestInformationCard } from "@src/features/role_request_details/components/role_request_information_card";
import { StandardRoleInformationCard } from "@src/features/role_request_details/components/standard_role_information_card/index";
import { StandardRoleInformationCardProps } from "@src/features/role_request_details/components/standard_role_information_card/standard_role_information_card";
import { StandardRoleSelectionCard } from "@src/features/role_request_details/components/standard_role_selection_card";
import { StandardRoleSelectionCardProps } from "@src/features/role_request_details/components/standard_role_selection_card/standard_role_selection_card";
import { useAppSelector, RootState } from "@src/setupStore";
import { RoleRequest } from "@src/types/role_request_types";

import { mapWorkloadTableRowData } from "../../utils/role_request_details_utils";

interface OverviewTabProps {
  request: RoleRequest;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ request }) => {
  const { isJointVentureProject } = useAppSelector(
    (state: RootState) => state.resourcePlanSliceV2
  );

  /**
   * Creating the card layout for the request status. For every status there are different card layouts with different cards.
   *
   * @param {string} status - The status of the request to create the layout for.
   *
   * @return {React.ReactElement[]} - The created grid layout
   */
  function getCardLayout(status: string): React.ReactElement[] {
    const standardRoleSelectionCardProps: StandardRoleSelectionCardProps = {
      standardRoleName: request?.standardRoleName,
      roleSpecificationName: request?.roleSpecificationName,
      basicRoleName: request?.basicRoleName,
    };

    const standardRoleInformationCardProps: StandardRoleInformationCardProps = {
      basicRoleName: request?.basicRoleName,
      roleSpecificationName: request?.roleSpecificationName,
      teamLeadName: request?.teamLead?.fullName,
    };

    const additionalInformationCardProps: AdditionalInformationCardProps = {
      projectIdentifier: request?.projectIdentifier,
      scope: request?.scope,
      specificRole: request?.specificRole,
      hasEditModal: request.status !== REQUEST_STATUS.DRAFT,
    };

    switch (status) {
      case REQUEST_STATUS.REQUESTED:
      case REQUEST_STATUS.IN_PROGRESS:
      case REQUEST_STATUS.DECLINED:
        return [
          <StandardRoleSelectionCard {...standardRoleSelectionCardProps} />,
          <RoleRequestInformationCard request={request} />,
          <AdditionalInformationCard {...additionalInformationCardProps} />,
        ];
      case REQUEST_STATUS.CONFIRMED:
      case REQUEST_STATUS.PROPOSED:
        return [
          <StandardRoleInformationCard {...standardRoleInformationCardProps} />,
          <RoleRequestInformationCard request={request} />,
          <ProposedEmployeeInformationCard
            status={request?.status}
            employee={request?.assignedEmployee}
          />,
          <AdditionalInformationCard {...additionalInformationCardProps} />,
        ];
      case REQUEST_STATUS.IN_RECRUITMENT:
        return [
          <StandardRoleInformationCard {...standardRoleInformationCardProps} />,
          <RoleRequestInformationCard request={request} />,
          <AdditionalInformationCard {...additionalInformationCardProps} />,
        ];
      case REQUEST_STATUS.DRAFT:
        return [
          <StandardRoleSelectionCard {...standardRoleSelectionCardProps} />,
          <AdditionalInformationCard {...additionalInformationCardProps} />,
        ];
      default:
        return [];
    }
  }

  return (
    <div className="overview-tab">
      <div className="overview-tab__cards">
        {getCardLayout(request.status).map(
          (component: React.ReactElement) => component
        )}
      </div>

      {request?.status === REQUEST_STATUS.DRAFT && isJointVentureProject && (
        <JointVentureCheckbox request={request} />
      )}

      <div>
        {!request.isOutdated && (
          <WorkloadTable requestRowData={mapWorkloadTableRowData(request)} />
        )}
      </div>
    </div>
  );
};

export default OverviewTab;
